import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts'
import { dashboardApi } from '../../api/endpoints'
import { Card, CardHeader, CardBody, KpiCard, AvatarIcon, ProgressBar } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { LoadingState, ErrorState, EmptyState } from '../../components/ui/EmptyState'
import { Table, THead, Th, Tr, Td } from '../../components/ui/Table'
import { MonthNavigator } from '../../components/ui/Navigation'
import { PageHeader } from '../../layout/PageHeader'
import { PrimeirosPassosCard } from '../tutorial/PrimeirosPassosCard'
import { useAuthStore } from '../../store/authStore'
import { formatMoney, formatDate } from '../../lib/utils'
import type { ResultadoMes } from '../../types'

const CHART_TOOLTIP = { background: '#1B2330', border: '1px solid #2D3748', borderRadius: 4, fontSize: 12 }
const DONUT_COLORS = ['#EF4444', '#3B82F6', '#22C55E']

const MESES_CURTOS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']

const TONE_TEXT = { income: 'text-income', expense: 'text-expense', info: 'text-info', warning: 'text-warning' } as const

const TYPE_META: Record<string, { label: string; tone: 'income' | 'expense' | 'info' | 'warning'; sign: string }> = {
  Income: { label: 'Receita', tone: 'income', sign: '+' },
  Expense: { label: 'Despesa', tone: 'expense', sign: '-' },
  Transfer: { label: 'Transferência', tone: 'info', sign: '' },
  Investment: { label: 'Investimento', tone: 'warning', sign: '' },
}

function PendentePrevisto({ tone, pendente, previsto, to, rendimentos }: { tone: 'income' | 'expense'; pendente: number; previsto: number; to: string; rendimentos?: number }) {
  return (
    <div className="mt-3 space-y-1 border-t border-border/60 pt-2 text-13">
      <Link to={to} className="flex items-center justify-between text-muted hover:text-white">
        <span>Pendente</span>
        <span className={`font-semibold ${tone === 'income' ? 'text-income' : 'text-expense'} opacity-80`}>{formatMoney(pendente)}</span>
      </Link>
      <div className="flex items-center justify-between text-muted">
        <span>Previsto</span>
        <span className="font-medium text-white">{formatMoney(previsto)}</span>
      </div>
      {!!rendimentos && rendimentos > 0 && (
        <div className="flex items-center justify-between text-income">
          <span><i className="ri-line-chart-line" aria-hidden /> Rendimentos</span>
          <span className="font-semibold">{formatMoney(rendimentos)}</span>
        </div>
      )}
    </div>
  )
}

const corDoResultado = (v: number) => (v > 0 ? 'text-income' : v < 0 ? 'text-expense' : 'text-white')
const corDoSaldo = (v: number) => (v < 0 ? 'text-expense' : 'text-white')
const comSinal = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${formatMoney(Math.abs(v))}`

/**
 * Resultado do mês (receitas menos despesas, pelo caixa) e o que ele carrega dos meses anteriores.
 * Realizado ao lado do previsto. Os atrasados ficam de fora dos números e aparecem numa linha à parte.
 */
function CardResultado({ r, mes, nomeMes }: { r: ResultadoMes; mes: number; nomeMes: string }) {
  const anterior = MESES_CURTOS[(mes + 10) % 12]
  return (
    <Card>
      <CardHeader title={`Resultado de ${nomeMes}`} />
      <CardBody>
        <div className="grid grid-cols-[1.6fr_1fr_1fr] items-baseline gap-x-4 gap-y-3 text-13">
          <span />
          <span className="text-right text-2xs text-muted">Realizado</span>
          <span className="text-right text-2xs text-muted">Previsto</span>

          <span className="text-muted">Acumulado até {anterior}</span>
          <span className={`text-right font-semibold ${corDoSaldo(r.acumuladoAnteriorRealizado)}`}>{formatMoney(r.acumuladoAnteriorRealizado)}</span>
          <span className={`text-right font-semibold ${corDoSaldo(r.acumuladoAnteriorPrevisto)}`}>{formatMoney(r.acumuladoAnteriorPrevisto)}</span>

          <div className="col-span-3 border-t border-border/60" />

          <span className="text-muted">Resultado do mês</span>
          <span className={`text-right font-semibold ${corDoResultado(r.resultadoMesRealizado)}`}>{comSinal(r.resultadoMesRealizado)}</span>
          <span className={`text-right font-semibold ${corDoResultado(r.resultadoMesPrevisto)}`}>{comSinal(r.resultadoMesPrevisto)}</span>

          {!!r.ajustesDoMes && (
            <>
              <span className="text-muted" title="Correções de saldo (reconciliação com o banco, saldo inicial). Não são receita nem gasto.">Ajustes de saldo</span>
              <span className={`text-right font-semibold ${corDoResultado(r.ajustesDoMes)}`}>{comSinal(r.ajustesDoMes)}</span>
              <span className={`text-right font-semibold ${corDoResultado(r.ajustesDoMes)}`}>{comSinal(r.ajustesDoMes)}</span>
            </>
          )}

          <div className="col-span-3 border-t border-border/60" />

          <span className="font-medium text-white">Acumulado com {nomeMes}</span>
          <span className={`text-right text-15 font-semibold ${corDoSaldo(r.acumuladoComMesRealizado)}`}>{formatMoney(r.acumuladoComMesRealizado)}</span>
          <span className={`text-right text-15 font-semibold ${corDoSaldo(r.acumuladoComMesPrevisto)}`}>{formatMoney(r.acumuladoComMesPrevisto)}</span>
        </div>

        {r.atrasadosLiquido !== 0 && (
          <Link
            to="/transacoes?situacao=atrasadas"
            className="mt-4 flex items-center justify-between gap-3 border-t border-dashed border-border/60 pt-3 text-2xs text-muted hover:text-white"
          >
            <span><i className="ri-information-line" aria-hidden /> Atrasados (não contam acima)</span>
            <span className={`flex items-center gap-1 font-semibold ${corDoResultado(r.atrasadosLiquido)}`}>
              {comSinal(r.atrasadosLiquido)} <i className="ri-arrow-right-s-line" aria-hidden />
            </span>
          </Link>
        )}
      </CardBody>
    </Card>
  )
}

function saudacao() {
  const h = new Date().getHours()
  if (h < 12) return 'Bom dia'
  if (h < 18) return 'Boa tarde'
  return 'Boa noite'
}

export function DashboardPage() {
  const userName = useAuthStore((s) => s.userName)
  const hoje = new Date()
  const [mes, setMes] = useState(hoje.getMonth() + 1)
  const [ano, setAno] = useState(hoje.getFullYear())

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['dashboard', mes, ano],
    queryFn: () => dashboardApi.get(mes, ano),
  })

  if (isLoading) return <><PageHeader title="Visão Geral" /><LoadingState /></>
  if (isError || !data) return <><PageHeader title="Visão Geral" /><ErrorState message="Erro ao carregar o dashboard." /></>

  const d = data
  const nomeMes = MESES_CURTOS[d.mes - 1]
  const at = d.pendencias
  // Do card para a lista já filtrada: o que somou é o que a lista mostra (visão de caixa: a fatura representa as compras no cartão).
  const listaCaixa = (tipo: 'receitas' | 'despesas', situacao: 'confirmadas' | 'pendentes') =>
    `/transacoes?tipo=${tipo}&situacao=${situacao}&mes=${d.mes}&ano=${d.ano}&caixa=1`
  const donutData = [
    { name: 'Gastos', value: Math.abs(d.gastosMes) },
    { name: 'Investimentos', value: d.investimentos },
    { name: 'Receitas', value: d.receitasMes },
  ]
  const donutTotal = donutData.reduce((s, x) => s + x.value, 0)

  return (
    <div className="space-y-6">
      <PageHeader title="Visão Geral" />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="text-base font-semibold text-white">{saudacao()}, {userName?.split(' ')[0] ?? 'bem-vindo'}!</h4>
          <p className="text-13 text-muted">Veja como estão suas finanças no mês selecionado.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <MonthNavigator mes={mes} ano={ano} onChange={(m, a) => { setMes(m); setAno(a) }} />
          <Link to="/transacoes" className="inline-flex h-[38px] items-center gap-2 rounded bg-income/10 px-4 text-13 font-medium text-income hover:bg-income hover:text-white">
            <i className="ri-add-circle-line text-base" aria-hidden /> Lançamentos
          </Link>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex h-[38px] w-[38px] items-center justify-center rounded bg-info/10 text-info hover:bg-info hover:text-white disabled:opacity-50"
            aria-label="Atualizar dashboard"
          >
            <i className={isFetching ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'} aria-hidden />
          </button>
        </div>
      </div>

      <PrimeirosPassosCard />

      {at.atrasadosQuantidade > 0 && (
        <Link to="/transacoes?situacao=atrasadas" className="flex items-center gap-3 rounded border border-expense/40 bg-expense/10 px-4 py-3 text-expense hover:bg-expense/15">
          <i className="ri-alarm-warning-line text-xl" aria-hidden />
          <div className="flex-1">
            <p className="text-13 font-semibold">{at.atrasadosQuantidade} lançamento{at.atrasadosQuantidade > 1 ? 's' : ''} atrasado{at.atrasadosQuantidade > 1 ? 's' : ''}</p>
            <p className="text-2xs opacity-90">
              {at.atrasadosDespesas > 0 ? `${formatMoney(at.atrasadosDespesas)} a pagar` : ''}
              {at.atrasadosDespesas > 0 && at.atrasadosReceitas > 0 ? ' · ' : ''}
              {at.atrasadosReceitas > 0 ? `${formatMoney(at.atrasadosReceitas)} a receber` : ''}
            </p>
          </div>
          <i className="ri-arrow-right-s-line text-xl" aria-hidden />
        </Link>
      )}

      <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          title="Patrimônio total"
          value={formatMoney(d.patrimonio)}
          icon="ri-wallet-3-line"
          tone="primary"
          change={{ value: `${d.variacaoPatrimonio >= 0 ? '+' : ''}${d.variacaoPatrimonio.toFixed(2)} %`, positive: d.variacaoPatrimonio >= 0 }}
          link={{ label: 'Ver contas', to: '/contas' }}
        />
        <KpiCard
          title={`Receitas de ${nomeMes} · realizado`} value={formatMoney(d.receitasMes)} icon="ri-arrow-left-down-line" tone="income"
          link={{ label: 'Ver realizadas', to: listaCaixa('receitas', 'confirmadas') }}
          footer={<PendentePrevisto tone="income" pendente={d.pendencias.receitasPendentes} previsto={d.pendencias.receitasPrevistas} to={listaCaixa('receitas', 'pendentes')} rendimentos={d.rendimentosMes} />}
        />
        <KpiCard
          title={`Gastos de ${nomeMes} · realizado`} value={formatMoney(d.gastosMes)} icon="ri-arrow-right-up-line" tone="expense"
          link={{ label: 'Ver realizados', to: listaCaixa('despesas', 'confirmadas') }}
          footer={<PendentePrevisto tone="expense" pendente={d.pendencias.gastosPendentes} previsto={d.pendencias.gastosPrevistos} to={listaCaixa('despesas', 'pendentes')} />}
        />
        <KpiCard
          title={`Saldo de ${nomeMes}`}
          value={formatMoney(d.saldoMes)}
          icon="ri-scales-3-line"
          tone={d.saldoMes >= 0 ? 'income' : 'expense'}
          link={{ label: 'Ver relatórios', to: '/relatorios' }}
        />
      </div>

      {d.resultado && <CardResultado r={d.resultado} mes={d.mes} nomeMes={nomeMes} />}

      <div className="grid gap-6 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader title="Fluxo de caixa" />
          <div className="grid grid-cols-2 border-b border-border/60 bg-surface2/40 text-center">
            <div className="border-r border-border/60 p-3">
              <h5 className="text-base font-semibold text-income">{formatMoney(d.receitasMes)}</h5>
              <p className="text-13 text-muted">Receitas no mês</p>
            </div>
            <div className="p-3">
              <h5 className="text-base font-semibold text-expense">{formatMoney(d.gastosMes)}</h5>
              <p className="text-13 text-muted">Gastos no mês</p>
            </div>
          </div>
          <CardBody>
            {d.fluxoCaixa.length === 0 ? (
              <EmptyState icon="ri-bar-chart-2-line" message="Sem movimentações para exibir." />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={d.fluxoCaixa} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" vertical={false} />
                  <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#8B949E' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#8B949E' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(Number(v) / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => formatMoney(Number(v))} contentStyle={CHART_TOOLTIP} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                  <Legend wrapperStyle={{ fontSize: 12, color: '#8B949E' }} iconType="circle" />
                  <Bar dataKey="receitas" name="Receitas" fill="#22C55E" radius={[3, 3, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="gastos" name="Gastos" fill="#EF4444" radius={[3, 3, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Distribuição do mês" />
          <CardBody>
            {donutTotal === 0 ? (
              <EmptyState icon="ri-pie-chart-line" message="Sem valores neste mês." />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={donutData} cx="50%" cy="50%" innerRadius={60} outerRadius={85} dataKey="value" strokeWidth={0} paddingAngle={2}>
                      {donutData.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => formatMoney(Number(v))} contentStyle={CHART_TOOLTIP} />
                  </PieChart>
                </ResponsiveContainer>
                <ul className="mt-2 space-y-3">
                  {donutData.map((item, i) => (
                    <li key={item.name}>
                      <div className="mb-1 flex items-center justify-between text-13">
                        <span className="flex items-center gap-2 text-muted">
                          <span className="h-2 w-2 rounded-full" style={{ background: DONUT_COLORS[i] }} /> {item.name}
                        </span>
                        <span className="font-medium text-white">{formatMoney(item.value)}</span>
                      </div>
                      <ProgressBar value={donutTotal ? (item.value / donutTotal) * 100 : 0} tone={i === 0 ? 'expense' : i === 1 ? 'info' : 'income'} />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </CardBody>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader
            title="Transações recentes"
            actions={<Link to="/transacoes" className="text-13 text-muted hover:text-primary">Ver todas <i className="ri-arrow-right-line align-middle" aria-hidden /></Link>}
          />
          {d.transacoesRecentes.length === 0 ? (
            <EmptyState icon="ri-exchange-dollar-line" message="Nenhuma transação ainda." />
          ) : (
            <Table>
              <THead>
                <tr><Th>Descrição</Th><Th>Categoria</Th><Th>Data</Th><Th className="text-right">Valor</Th></tr>
              </THead>
              <tbody>
                {d.transacoesRecentes.slice(0, 6).map((t) => {
                  const meta = TYPE_META[t.type] ?? { label: t.type, tone: 'info' as const, sign: '' }
                  return (
                    <Tr key={t.id}>
                      <Td>
                        <div className="flex items-center gap-3">
                          <AvatarIcon icon={meta.tone === 'income' ? 'ri-arrow-left-down-line' : 'ri-arrow-right-up-line'} tone={meta.tone} size="sm" />
                          <span className="font-medium text-white">{t.description}</span>
                        </div>
                      </Td>
                      <Td className="text-muted">{t.category ?? '—'}</Td>
                      <Td className="text-muted">{formatDate(t.date)}</Td>
                      <Td className={`text-right font-semibold ${TONE_TEXT[meta.tone]}`}>{meta.sign}{formatMoney(Math.abs(t.amount))}</Td>
                    </Tr>
                  )
                })}
              </tbody>
            </Table>
          )}
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Metas" actions={<Link to="/metas" className="text-13 text-muted hover:text-primary">Ver todas</Link>} />
            <CardBody className="space-y-4">
              {d.metas.length === 0 && <p className="py-4 text-center text-13 text-muted">Nenhuma meta cadastrada.</p>}
              {d.metas.slice(0, 4).map((g) => (
                <div key={g.id}>
                  <div className="mb-1.5 flex items-center justify-between gap-2">
                    <span className="flex min-w-0 items-center gap-2 text-13 text-white">
                      <span>{g.icon ?? '🎯'}</span>
                      <span className="truncate">{g.name}</span>
                    </span>
                    <span className="text-13 font-medium text-white">{g.percentual.toFixed(0)}%</span>
                  </div>
                  <ProgressBar value={g.percentual} />
                  <div className="mt-1 flex justify-between text-2xs text-muted">
                    <span>{formatMoney(g.currentAmount)}</span>
                    <span>{formatMoney(g.targetAmount)}</span>
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Contas"
              actions={
                <span className="flex items-center gap-3">
                  <span className="text-13 text-muted">Investido <strong className="text-white">{formatMoney(d.investimentos)}</strong></span>
                  <Link to="/contas" className="text-13 text-muted hover:text-primary">Ver todas</Link>
                </span>
              }
            />
            <CardBody className="space-y-3">
              {d.contas.length === 0 && <p className="py-4 text-center text-13 text-muted">Nenhuma conta cadastrada.</p>}
              {d.contas.slice(0, 5).map((c) => (
                <div key={c.id} className="flex items-center gap-3">
                  <AvatarIcon icon="ri-bank-line" tone="info" size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-13 font-medium text-white">{c.name}</p>
                    <p className="truncate text-2xs text-muted">{c.institution}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-13 font-semibold ${c.balance >= 0 ? 'text-white' : 'text-expense'}`}>{formatMoney(c.balance)}</p>
                    {c.isConnected && <Badge variant="income">Conectada</Badge>}
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  )
}
